import { addBreadcrumb } from "@sentry/vue";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { GraphQLClient, type RequestMiddleware } from "graphql-request";

import { app } from "@/firebase";
import { getSdk } from "@/sdk";
import { extractTokenExpiryAndIssuedAt } from "@/utils/auth.utils";

const auth = getAuth(app);

export const getToken = async (forceRefresh?: boolean) => {
	return new Promise((resolve) => {
		onAuthStateChanged(auth, async (user) => {
			if (user) {
				const token = await user.getIdToken(forceRefresh ?? undefined);
				addBreadcrumb({
					category: "sentry.transaction",
					data: {
						token: token,
						expiry: extractTokenExpiryAndIssuedAt(token).expiry,
					},
					message: "Token issued",
					level: "debug",
				});
				resolve(token);
			} else resolve(false);
		});
	});
};

const client = (async () => {
	try {
		const requestMiddleware: RequestMiddleware = async (request) => {
			return {
				...request,
				headers: {
					...request.headers,
					Authorization: `Bearer ${await getToken()}`,
					"x-hasura-role": "driver",
				},
			};
		};
		const graphQLClient = new GraphQLClient(
			import.meta.env.VITE_GRAPHQL_SCHEMA_PATH,
			{ requestMiddleware },
		);

		return getSdk(graphQLClient);
	} catch (error: any) {
		const gqlErrors = error.response?.errors ?? [];

		for (const err of gqlErrors) {
			const code = err.extensions?.code;
			const message = err.message;

			switch (code) {
				case "permission-denied":
					console.warn("[Hasura] Permission denied:", message);
					// e.g. router.push('/login') if using Vue Router
					break;
				case "validation-failed":
					console.warn("[Hasura] Validation failed:", message);
					break;
				default:
					console.error("[Hasura] GraphQL Error:", message);
					break;
			}
		}
		if (error.request && !error.response) {
			console.error("[Network] Failed request:", error.message);
		} else if (!gqlErrors.length) {
			console.error("[Unknown Error]", error);
		}
		throw error;
	}
})();

export default client;
